import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { singletonHook } from 'react-singleton-hook';

import { IAthleteRole } from '@interfaces/lookups';
import { handleGetAthleteRoles } from '@store/features/athleteRoles/actions';
import {
  athleteRolesErrorSelector,
  athleteRolesIsLoadingSelector,
  athleteRolesIsReadySelector,
  athleteRolesSelector,
} from '@store/features/athleteRoles/selectors';
import { userDataSelector } from '@store/features/auth/user/selectors';
import useDispatch from '@store/hooks/useDispatch';

import useFetchLookupData from './useFetchLookupData';

const useAthleteRoles = (): IAthleteRole[] => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const data = useSelector(athleteRolesSelector);
  const isLoading = useSelector(athleteRolesIsLoadingSelector);
  const isReady = useSelector(athleteRolesIsReadySelector);
  const error = useSelector(athleteRolesErrorSelector);
  const user = useSelector(userDataSelector);

  const handleFetch = useCallback(() => {
    user?.showRole && user?.organizationId && dispatch(handleGetAthleteRoles(user.organizationId));
  }, [dispatch, user]);

  useFetchLookupData({
    isReady,
    isLoading,
    error: error && t('errors.athleteRolesError'),
    handleFetch,
  });

  return data;
};

export default singletonHook([], useAthleteRoles);
